// FleetMap: what an empty workspace shows (a new one, or a pane with none):
// the range's AI work as one board (FlowBoard), and under it, scrolled to,
// the cases that need a person (WelcomeTriage), then the
// work by every kind of thing it reached (FleetInventory), the pane's
// foot naming each part and lighting the one in view, and the search held at
// the top of the page as it scrolls. The board fills the
// pane's first screen and stretches with it: every column, card and strand
// is laid out for the room the pane has. Everything on the page reads what
// the workspace's one search reaches, and a card or a row picked narrows
// that search.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FlowBoard, { STEPS, useAgentHues } from "./FlowBoard.jsx";
import ScrollArea from "./ScrollArea.jsx";
import FleetInventory, { KINDS } from "./FleetInventory.jsx";
import WelcomeTriage, { TRIAGE_PART } from "./WelcomeTriage.jsx";
import WelcomeFrame from "./welcome/WelcomeFrame.jsx";
import { ViewSwitch } from "./welcome/parts.jsx";
import { WELCOME_VIEWS, keepWelcomeView, readWelcomeView } from "./welcome/views.js";
import { FINDER_BOX_H, withoutClause } from "./SearchPalette.jsx";
import { useQuery } from "../lib/dataClient.js";
import { useSessionFacts } from "../lib/sessionFacts.js";
import { appendClause, holdsClause, setWorkspaceSearch, useReviewReach, useSearchScope, useWorkspacePicks } from "../lib/workspaceSearch.js";
import { rangeBoundsMs } from "../lib/analyticsClient.js";
import { toApiRange } from "../lib/shell.js";
import { pipelineFilterText } from "../lib/queryPipeline.js";
import { usePaneFoot } from "../lib/paneFoot.js";

const MARGIN = 16;
// The search box held at the top of the page, on the page's ground to
// HEAD_GAP under it, so what scrolls goes under it: at the top of the page
// that gap is room the board leaves above its captions, so the ground hides
// nothing there. What scrolls the page to a part stops it under the box.
const HEAD_GAP = 10;
const HEAD = MARGIN + FINDER_BOX_H + HEAD_GAP;
// The page scrolls as every mode's sidebar does, its bar laid over it; the
// parts strip as the tab strips do, sideways, its bar shown while it moves.
const PAGE_SCROLL = Object.freeze({ overflow: { x: "hidden" } });
const PARTS_SCROLL = Object.freeze({ overflow: { x: "scroll", y: "hidden" }, scrollbars: { autoHide: "scroll", autoHideDelay: 400 } });

// The board's middle column says who ran the work: the groups they are in,
// or the people, as this browser last chose (people at first).
const MIDDLE = ["group", "person"];
const MIDDLE_KEY = "origin-welcome-middle-v1";
const SWAPS = [null, MIDDLE.map((k) => STEPS[k]), null];
function readMiddle() {
  try {
    const saved = globalThis.localStorage?.getItem(MIDDLE_KEY);
    return MIDDLE.includes(saved) ? saved : "person";
  } catch {
    return "person";
  }
}

// The page's parts, a hairline cell each, as the pane's own foot cells are
// drawn, the part in view lit as the app's rail lights its mode (the pressed
// tint and a green bar, here along the cell's foot): in the pane's foot band
// where there is one, else in a band of the page's own. Too many for the
// strip's width, it follows the page: the part in view stays in sight, with
// room for a part either side. A click goes to a part.
const BAND_H = 32;
// The sections under the board, in the order the page goes down; the groups
// only for a tenant whose directory gives someone a department.
const KINDS_NO_GROUPS = KINDS.filter((k) => k.id !== "groups");
const SECTIONS = [TRIAGE_PART, ...KINDS];
const SECTIONS_NO_GROUPS = [TRIAGE_PART, ...KINDS_NO_GROUPS];
const partsOf = (sections) => [{ id: "overview", label: "Overview" }, ...sections];
// The directory changes slowly: whether it has groups is read again at most
// this often.
const DIRECTORY_STALE_MS = 10 * 60_000;
const PART_CELL = "flex shrink-0 items-center whitespace-nowrap border-l border-[color:var(--line-soft)] px-[10px] leading-[16px] transition-colors";
function PartsBar({ parts, active, onGo }) {
  const strip = useRef(null);
  const setStrip = useCallback((instance) => { strip.current = instance ? instance.elements().viewport : null; }, []);
  useEffect(() => {
    const view = strip.current;
    const cell = view?.querySelector(`[data-fleet-nav-part="${active}"]`);
    if (!view || !cell || view.scrollWidth <= view.clientWidth) return;
    const v = view.getBoundingClientRect();
    const c = cell.getBoundingClientRect();
    const x0 = c.left - v.left + view.scrollLeft;
    const room = Math.min(80, Math.max(0, (view.clientWidth - c.width) / 2));
    const left = x0 - room < view.scrollLeft ? x0 - room
      : x0 + c.width + room > view.scrollLeft + view.clientWidth ? x0 + c.width + room - view.clientWidth : null;
    if (left != null) view.scrollTo?.({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);
  return (
    <ScrollArea instanceRef={setStrip} options={PARTS_SCROLL} observeMutations={false} className="min-w-0 border-r border-[color:var(--line-soft)]" contentClassName="h-full">
      <nav className="flex h-full items-stretch text-[11px]" aria-label="Parts of the overview" data-fleet-nav="">
        {parts.map((part) => {
          const on = active === part.id;
          return (
            <button
              key={part.id}
              type="button"
              onClick={() => onGo(part.id)}
              aria-current={on ? "true" : undefined}
              className={`${PART_CELL} ${on ? "[background-image:linear-gradient(var(--green-soft),var(--green-soft))] text-[color:var(--ink)] shadow-[inset_0_-2px_0_var(--green)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
              data-fleet-nav-part={part.id}
            >
              {part.label}
            </button>
          );
        })}
      </nav>
    </ScrollArea>
  );
}

export default function FleetMap({ range = "1M", lost = false, onStartSearch, startLabel, startHint }) {
  const scope = useSearchScope(range);
  const metrics = useQuery("getOverviewMetrics", { scopePath: scope.scopePath, range, query: scope.steer, byTrace: true }, { enabled: scope.ready });
  const read = useSessionFacts(scope, range);
  // What the board last read stays up, dimmed, while the next reading is
  // under way, so narrowing the search moves the board rather than blanking
  // it; before any reading there is the board's placeholder.
  const last = useRef({ facts: null, metrics: null });
  if (read.status === "done") last.current.facts = read;
  if (metrics.data) last.current.metrics = metrics;
  const facts = read.status === "loading" && last.current.facts ? { ...last.current.facts, stale: true } : read;
  const shownMetrics = metrics.data || !last.current.metrics ? metrics : { ...last.current.metrics, stale: true };
  // Drawn from the newest of what the search reaches, the board counts only
  // those: where review left every one, for the search box to count and the
  // review cards to say the whole of (a card of 2 drawn here, 7 in all, is
  // 7 drilled down to).
  const flagged = useReviewReach(range, scope.q, { enabled: !!facts.capped && scope.ready });
  const reached = useMemo(() => {
    if (!flagged) return null;
    const total = Number(metrics.data?.sessions);
    const some = flagged.met + flagged.not_verified + flagged.not_met + flagged.set_aside;
    return { ...flagged, none: Number.isFinite(total) ? Math.max(0, total - some) : null };
  }, [flagged, metrics.data]);
  // Groups only where the directory gives someone a department (until that is
  // read, none): elsewhere the board, the sections and the finder go by people.
  const directory = useQuery("getDirectoryGroups", {}, { staleMs: DIRECTORY_STALE_MS });
  const groups = !!directory.data?.any;
  const kinds = groups ? KINDS : KINDS_NO_GROUPS;
  const below = groups ? SECTIONS : SECTIONS_NO_GROUPS;
  const partList = useMemo(() => partsOf(below), [below]);
  // Which agents ran the work, the groups or the people who ran it, and where
  // review left it.
  const [middle, setMiddle] = useState(readMiddle);
  const shownMiddle = groups ? middle : "person";
  const steps = useMemo(() => [STEPS.agent, STEPS[shownMiddle], STEPS.review], [shownMiddle]);
  const onSwap = useCallback((_ci, def) => {
    const key = MIDDLE.find((k) => STEPS[k] === def);
    if (!key) return;
    setMiddle(key);
    try {
      globalThis.localStorage?.setItem(MIDDLE_KEY, key);
    } catch {
      // Not kept past this page: the choice still stands while it is open.
    }
  }, []);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const bounds = useMemo(() => {
    const { fromMs, toMs } = rangeBoundsMs(typeof range === "string" ? toApiRange(range) : range);
    return { from: fromMs, to: Math.min(toMs, Date.now()) };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // What scrolls: the board's screen, then the sections; the overlay
  // scrollbars' viewport, marked for what looks for the page's scroll.
  const viewRef = useRef(null);
  const [scroller, setScroller] = useState(null);
  const setView = useCallback((instance) => {
    const el = instance ? instance.elements().viewport : null;
    el?.setAttribute?.("data-fleet-scroll", "");
    // What brings a part of the page into view stops it under the search.
    if (el?.style) el.style.scrollPaddingTop = `${HEAD}px`;
    viewRef.current = el;
    setScroller(el);
  }, []);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = scroller;
    if (!el) return undefined;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scroller]);
  // A card or a row picked narrows the search to it, and everything on the
  // page and in the sidebar's list follows; the one it is narrowed to
  // already, picked, widens it back out.
  const onPick = useCallback((clause) => setWorkspaceSearch((t) => (holdsClause(t, clause) ? withoutClause(t, clause) : appendClause(t, clause))), []);
  // Where each section stands, for the bar at the foot, and the part being
  // read: the last whose top is at the top of the view, the last of all at
  // the end. A part gone to from the bar stays lit while the page moves to
  // it, though a short last part can only come part of the way up.
  const sections = useRef(new Map());
  const sectionRef = useCallback((id, el) => {
    if (el) sections.current.set(id, el);
    else sections.current.delete(id);
  }, []);
  const topOf = useCallback((id) => {
    const el = viewRef.current;
    const part = sections.current.get(id);
    return el && part ? part.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop : 0;
  }, []);
  const [active, setActive] = useState("overview");
  const held = useRef(null);
  // Where the page is moving to, gone to from the bar: a section read as it
  // comes near grows under the move, and a move aimed once stopped that much
  // short of the part. Aimed again as it goes, until it stops.
  const aim = useRef(null);
  const aimAt = useCallback((id) => (id === "overview" ? 0 : Math.max(0, topOf(id) - HEAD)), [topOf]);
  useEffect(() => {
    const el = scroller;
    if (!el) return undefined;
    let frame = 0;
    const spy = () => {
      frame = 0;
      if (held.current) return;
      const line = el.scrollTop + HEAD + 40;
      let at = "overview";
      for (const part of below) if (sections.current.has(part.id) && topOf(part.id) <= line) at = part.id;
      if (el.scrollTop > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 2) at = below[below.length - 1].id;
      setActive(at);
    };
    const onScroll = () => {
      // The page moving to a part gone to: held until it stops a moment, and
      // aimed again where the part now stands.
      if (held.current) {
        clearTimeout(held.current);
        held.current = setTimeout(() => { held.current = null; aim.current = null; }, 200);
        const a = aim.current;
        const top = a ? aimAt(a.id) : null;
        if (a && Math.abs(top - a.top) > 1) {
          a.top = top;
          el.scrollTo?.({ top, behavior: "smooth" });
        }
      }
      if (!frame) frame = requestAnimationFrame(spy);
    };
    // The reader taking the page over lets the aim go.
    const letGo = () => { aim.current = null; };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", letGo, { passive: true });
    el.addEventListener("touchstart", letGo, { passive: true });
    el.addEventListener("keydown", letGo);
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", letGo);
      el.removeEventListener("touchstart", letGo);
      el.removeEventListener("keydown", letGo);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scroller, topOf, aimAt, below]);
  const goTo = useCallback((id) => {
    const top = aimAt(id);
    setActive(id);
    aim.current = { id, top };
    clearTimeout(held.current);
    held.current = setTimeout(() => { held.current = null; aim.current = null; }, 200);
    viewRef.current?.scrollTo?.({ top, behavior: "smooth" });
  }, [aimAt]);
  // A section's row drilled down to (or back out of) narrows the search, and
  // the page goes back up to the board, to see what it narrowed to.
  const onDrill = useCallback((clause) => {
    onPick(clause);
    viewRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [onPick]);
  // The finder hands over the search as it stands at the keypress (the
  // scope trails the typing), with the sessions picked into it; once started
  // from, the picks are let go.
  const [picks, setPicks] = useWorkspacePicks();
  // The opener as it stands, so the search box's start does not change (and
  // the box redraw) each time the pane does.
  const startRef = useRef(onStartSearch);
  startRef.current = onStartSearch;
  const onStart = useCallback((text) => {
    const q = pipelineFilterText(text ?? scope.q).trim();
    if (!q && !picks.length) return;
    startRef.current?.(q, picks);
    setPicks([]);
  }, [scope.q, picks, setPicks]);

  // The board takes the first screen; the sections start under it. The
  // parts go in the pane's foot, or a band of the page's own without one.
  const foot = usePaneFoot();
  const [searchHost, setSearchHost] = useState(null);
  const boardW = Math.max(480, size.w - 2 * MARGIN);
  const boardH = Math.max(560, size.h - 2 * MARGIN);
  const parts = <PartsBar parts={partList} active={active} onGo={goTo} />;
  // The agents' colours, the board's and the Agents section's alike.
  const hues = useAgentHues(facts);
  // How the first screen shows the work: the flow board, or another of the
  // views tried beside it, named under it.
  const [viewId, setViewId] = useState(readWelcomeView);
  const onView = useCallback((id) => {
    setViewId(id);
    keepWelcomeView(id);
  }, []);
  const view = WELCOME_VIEWS.find((v) => v.id === viewId) || WELCOME_VIEWS[0];
  const views = <ViewSwitch views={WELCOME_VIEWS} view={view.id} onView={onView} />;
  return (
    <div className="flex h-full min-h-0 flex-col" data-workspace-overview="">
      <ScrollArea instanceRef={setView} options={PAGE_SCROLL} observeMutations={false} className="min-h-0 flex-1 bg-[color:var(--workspace-bg)]" contentClassName="isolate">
        {size.w ? (
          <>
            <div className="sticky top-0 z-[40] h-0" data-board-search-dock="">
              <div className="absolute inset-x-0 top-0 bg-[color:var(--workspace-bg)]" style={{ height: HEAD }} aria-hidden="true" data-board-search-ground="" />
              <div ref={setSearchHost} className="absolute" style={{ left: MARGIN, top: MARGIN, width: boardW }} data-board-search-host="" />
            </div>
            <div className="relative" style={{ height: MARGIN + boardH + MARGIN }}>
              <div className="absolute" style={{ left: MARGIN, top: MARGIN }}>
                {view.Body ? (
                  <WelcomeFrame
                    view={view}
                    facts={facts}
                    width={boardW}
                    height={boardH}
                    range={range}
                    bounds={bounds}
                    metrics={shownMetrics}
                    notice={lost ? "The workspace this pane showed is no longer kept here." : null}
                    reached={reached}
                    onPick={onPick}
                    onStart={onStart}
                    startLabel={startLabel}
                    startHint={startHint}
                    searchHost={searchHost}
                    hues={hues}
                    groups={groups}
                    onShowAll={goTo}
                    aside={views}
                  />
                ) : (
                  <FlowBoard
                    facts={facts}
                    steps={steps}
                    width={boardW}
                    height={boardH}
                    range={range}
                    metrics={shownMetrics}
                    notice={lost ? "The workspace this pane showed is no longer kept here." : null}
                    reached={reached}
                    onPick={onPick}
                    onStart={onStart}
                    startLabel={startLabel}
                    startHint={startHint}
                    searchHost={searchHost}
                    hues={hues}
                    groups={groups}
                    swaps={groups ? SWAPS : null}
                    onSwap={onSwap}
                    aside={views}
                  />
                )}
              </div>
            </div>
            <div className="grid gap-[40px]" style={{ padding: `${MARGIN}px ${MARGIN}px 48px` }}>
              <WelcomeTriage scope={scope} range={range} root={scroller} hold={read.status === "loading"} sectionRef={sectionRef} />
              <FleetInventory kinds={kinds} scope={scope} width={boardW} root={scroller} hold={read.status === "loading"} onDrill={onDrill} sectionRef={sectionRef} bounds={bounds} hues={hues} range={range} />
            </div>
          </>
        ) : null}
      </ScrollArea>
      {foot ? createPortal(parts, foot) : (
        <div className="flex shrink-0 justify-center border-t border-[color:var(--line-soft)] bg-[color:var(--bg)]" style={{ height: BAND_H }}>{parts}</div>
      )}
    </div>
  );
}
